import React, { useContext } from "react";

export interface UserSettings {
  darkMode: boolean;
  fontSize: number;
  showFootnotes: boolean;
  showVerseNumbers: boolean;
}

interface UserSettingsStore {
  setItem: (key: string, value: any) => Promise<void>;
  getItem: (key: string) => Promise<any>;
}

interface UserSettingsProps {
  store: UserSettingsStore;
  storeKeyPrefix: string;
}

interface UserSettingsState extends UserSettings {
  settingsLoaded: boolean;
  updateSetting: (key: keyof UserSettings, value: any) => void;
}

export const defaultUserSettings: Omit<UserSettingsState, "settingsLoaded"> = {
  darkMode: false,
  fontSize: 18,
  showFootnotes: true,
  showVerseNumbers: true,
  updateSetting: (key, value) => {}
};

export const UserSettingsContext = React.createContext(defaultUserSettings);

export const UserSettingsConsumer = UserSettingsContext.Consumer;

export class UserSettingsProvider extends React.Component<
  UserSettingsProps,
  UserSettingsState
> {
  static defaultProps = {
    storeKeyPrefix: "userSettings"
  };

  constructor(props: UserSettingsProps) {
    super(props);
    this.state = {
      settingsLoaded: false,
      ...defaultUserSettings,
      updateSetting: (settingName, newValue) => {
        if (settingName === "fontSize" && (newValue < 12 || newValue > 24)) {
          return; // ignore request if invalid font size
        }
        props.store.setItem(`${props.storeKeyPrefix}.${settingName}`, newValue);
        this.setState({ [settingName]: newValue } as any);
      }
    };
  }

  async getPersistedSettings(): Promise<Partial<UserSettings>> {
    const settingKeys: string[] = Object.keys(defaultUserSettings);
    const persistedSettingValues = await Promise.all(
      settingKeys.map(key =>
        this.props.store.getItem(`${this.props.storeKeyPrefix}.${key}`)
      )
    );

    return settingKeys.reduce((acc, curr, index) => {
      const persistedValue = persistedSettingValues[index];
      if (persistedValue !== null) acc[curr] = persistedValue;
      return acc;
    }, {} as any);
  }

  async componentDidMount() {
    try {
      const settings = await this.getPersistedSettings();
      this.setState({ settingsLoaded: true, ...settings } as UserSettings);
    } catch (err) {
      console.error(err);
      this.setState({ settingsLoaded: true });
    }
  }

  render() {
    const { settingsLoaded, ...rest } = this.state;

    return (
      <UserSettingsContext.Provider value={rest}>
        {settingsLoaded && this.props.children}
      </UserSettingsContext.Provider>
    );
  }
}

export const useUserSettings = () => useContext(UserSettingsContext);
